import { FormSectionHandler } from '../entity.handler';
import { ExportTaskResponse, ExportTaskUpdateRequest, IdName } from '@key-telematics/fleet-api-client';
import { AdminEditorService } from '../../admin.service';
import { FormBuilderField } from 'app/shared/components/form-builder';
import { TextField, EntityUtils, ComboField, ComboValueHandlers } from '../utils';
import { ICONS } from '../icons';

interface EditorValues {
    name?: string;
    client?: IdName;
    state?: string;
    deliveryMethod?: string;
    documentTypes?: string[];
    assets?: any[];
}

export class ExportTaskDetailsSectionHandler implements FormSectionHandler<ExportTaskResponse, EditorValues> {

    type = 'form';
    icon = ICONS.exporttask;
    bulk = false;

    title = this.translate('TITLE');
    description = this.translate('DESC');

    actions = [
        { id: 'credentials', name: this.translate('CREDENTIALS.TITLE'), showAlways: true },
    ];

    constructor(
        public admin: AdminEditorService,
        public ownerId: string,
        private creating: boolean
    ) {
    }

    private properties = ['name', 'client', 'deliveryMethod', 'documentTypes', 'assets'];

    translate(key: string, values?: Object): string {
        return this.admin.translate(`EDITORS.EXPORTTASK.DETAILS.${key}`, values);
    }

    applyUpdate(entity: ExportTaskResponse, payload: ExportTaskUpdateRequest): Promise<ExportTaskResponse> {
        return this.admin.applyUpdate(api => api.entities.updateExportTask(entity.id, payload));
    }

    mapEntityToEditor(entity: ExportTaskResponse): Promise<EditorValues> {
        return Promise.resolve(EntityUtils.mergeFields(this.properties, entity, {}));
    }

    async mapEditorToEntity(entity: ExportTaskUpdateRequest, values: EditorValues): Promise<ExportTaskUpdateRequest> {
        const result = EntityUtils.mergeFields<ExportTaskUpdateRequest>(this.properties, values, entity);
        if (result.deliveryMethod === 'firehose') {
            result.client = { id: this.ownerId };
        }
        return result;
    }

    async getFields(entity: ExportTaskResponse): Promise<FormBuilderField[]> {

        const company = await this.admin.getCached(`getCompany/${this.ownerId}`, api => api.accounts.getCompany(this.ownerId));

        const clientFilter = {
            type: 'clientfilter', id: 'client', title: this.translate('FIELDS.CLIENT'), required: true, max: 1,
            options: { ownerId: this.ownerId },
            getText: (field, values) => values[field.id].name, readonly: !this.creating,

        };

        const modes = [
            ['vendor', 'client'].includes(company.type) && { key: 'http', value: this.admin.translate(`EDITORS.EXPORTTASK.MODES.HTTP`), fields: [clientFilter] },
            (!this.creating || this.admin.user.owner.type === 'system') && { key: 'firehose', value: this.admin.translate(`EDITORS.EXPORTTASK.MODES.FIREHOSE`) },
            !this.creating && { key: 'stream', value: this.admin.translate(`EDITORS.EXPORTTASK.MODES.STREAM`), fields: [clientFilter] },
            !this.creating && { key: 'ftp', value: this.admin.translate(`EDITORS.EXPORTTASK.MODES.FTP`), fields: [clientFilter] },
        ].filter(x => x);

        const fields: FormBuilderField[] = [
            TextField('name', this.translate('FIELDS.NAME'), true, 1, 255),
            { ...ComboField('deliveryMethod', this.translate('FIELDS.MODE'), true, modes, ComboValueHandlers), readonly: !this.creating },
        ].filter(x => x);
        return EntityUtils.prepareBulk(!entity, [], fields);
    }


    async actionTriggered(entity: ExportTaskResponse, action: string): Promise<void> {
        if (action === 'credentials') {
            let message = this.translate('CREDENTIALS.NONE');
            switch (entity.deliveryMethod) {
                case 'http':
                    message = `**${this.translate('CREDENTIALS.ENDPOINT')}:** ${entity.endpoint}\n\n**${this.translate('CREDENTIALS.API_KEY')}:** ${entity.password}`;
                    break;
                case 'stream':
                    message = `**${this.translate('CREDENTIALS.ENDPOINT')}:** ${entity.endpoint}\n\n**${this.translate('CREDENTIALS.USERNAME')}:** ${entity.username}\n\n**${this.translate('CREDENTIALS.PASSWORD')}:** ${entity.password}`;
                    break;
                case 'firehose':
                    message = `${this.translate('CREDENTIALS.S3_POLICY')}\n<pre>${JSON.stringify(JSON.parse(entity.endpoint || '{}'), null, 2)}</pre>`;
                    break;
            }
            await this.admin.modal.alert(message, this.translate('CREDENTIALS.TITLE'));
        }
    }

}
